/**
 * TeamIdentity — vertical crest + name block.
 *
 * Renamed from `widgets/ihs/TeamHeader.tsx` (Phase 0b). The "Header"
 * name collided with `SiteHeader` (page chrome) — this primitive is a
 * tiny team-identity stack: circular crest above, bold name below,
 * centred (or left/right aligned) at three size presets.
 *
 * Brand-neutral: when no badge URL is provided the underlying
 * `<TeamCrest>` falls back to initials. The render layer never invokes
 * a tenant-specific badge resolver — engines ship URLs.
 *
 * Shape: { name: string; badge?: string; size?: 'sm'|'md'|'lg'; align?: 'center'|'left'|'right' }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { TeamCrest } from './TeamCrest.js';
import styles from './TeamIdentity.module.css';

const dataShape = z.object({
  name: z.string(),
  /** Optional badge URL. Falls back to initials when absent. */
  badge: z.string().url().optional(),
  size: z.enum(['sm', 'md', 'lg']).optional(),
  align: z.enum(['center', 'left', 'right']).optional(),
});

export const META: PrimitiveMeta = {
  name: 'team-identity',
  description: 'Vertical crest + bold team name stack at sm/md/lg.',
  composable: true,
  dataShape,
  densities: ['standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
  composes: ['team-crest'],
};

interface TeamIdentityProps {
  name: string;
  /** Optional badge URL. Falls back to initials when absent. */
  badge?: string;
  size?: 'sm' | 'md' | 'lg';
  align?: 'center' | 'left' | 'right';
}

const BADGE_SIZE: Record<NonNullable<TeamIdentityProps['size']>, number> = {
  sm: 32,
  md: 48,
  lg: 64,
};

export function TeamIdentity({
  name,
  badge,
  size = 'md',
  align = 'center',
}: TeamIdentityProps): ReactElement {
  const sizeClass =
    size === 'sm' ? styles.sm : size === 'lg' ? styles.lg : styles.md;
  const alignClass =
    align === 'left'
      ? styles.left
      : align === 'right'
        ? styles.right
        : styles.center;

  return (
    <div className={`${styles.identity} ${alignClass}`}>
      <TeamCrest
        {...(badge !== undefined ? { src: badge } : {})}
        name={name}
        size={BADGE_SIZE[size]}
      />
      <span className={`${styles.name} ${sizeClass}`}>{name}</span>
    </div>
  );
}

export default TeamIdentity;
